import { gql } from '@apollo/client';
import { WarningRounded } from '@mui/icons-material';
import { Avatar } from '@mui/material';
import React from 'react';

import { formatInitials } from '../../utils/formatting';
import { ReturnComponent } from '../../utils/reactTypes';
import { NONE_TEXT } from '../displayFields/AxleInfoField';
import { AxleChip } from '../displayFields/chips';

export const ClinicianChipFragment = gql`
    fragment ClinicianChipFragment on Clinician {
        id
        firstName
        lastName
        logoUrl
    }
`;

export interface IClinicianChip {
    id: string;
    firstName: string;
    lastName: string;
    logoUrl: string | null;
}

export interface IClinicianChipProps {
    clinician: IClinicianChip | null;
    showLink: boolean;
    wrap?: boolean;
    services?: {
        id: string;
        name: string;
    }[];
    showServices?: boolean;
    color?: string;
    title?: string;
    onMouseEnter?: (event: React.MouseEvent<HTMLElement>) => void;
    onMouseLeave?: (event: React.MouseEvent<HTMLElement>) => void;
}

export const ClinicianChip = ({
    clinician,
    showLink,
    wrap = false,
    services = [],
    showServices = false,
    color,
    title,
    onMouseEnter,
    onMouseLeave,
}: IClinicianChipProps): ReturnComponent => {
    const serviceLabel =
        showServices && services.length > 0
            ? ' (' + [...new Set(services.map((service) => service.name))].join(', ') + ')'
            : '';
    if (!clinician) return <AxleChip icon={<WarningRounded />} label={NONE_TEXT + serviceLabel} />;
    const name = `${clinician.firstName} ${clinician.lastName}`.trim();
    const label = `${name} ${serviceLabel}${title ? ` - ${title}` : ''}`;
    return (
        <AxleChip
            avatar={
                <Avatar src={clinician.logoUrl ?? undefined}>
                    {formatInitials(clinician.firstName, clinician.lastName)}
                </Avatar>
            }
            label={label}
            link={showLink ? `/clinicians/${clinician.id}` : undefined}
            sx={{
                backgroundColor: color ? 'FFFFFF' : undefined,
                color: color,
                border: color ? `2px solid ${color}` : undefined,
                fontWeight: color ? '500' : undefined,
                ...(wrap && {
                    // If the label wraps across multiple lines, make sure all lines get fitted.
                    height: '100%',
                    // We need this, otherwise if the label is all on one line, the chip is too thin,
                    //  and looks weird in comparison to other chips.
                    minHeight: '32px',
                    '& .MuiChip-label': {
                        whiteSpace: 'normal',
                        textAlign: 'left',
                        lineHeight: '1.25em',
                    },
                }),
            }}
            onMouseEnter={onMouseEnter}
            onMouseLeave={onMouseLeave}
        />
    );
};
